// Free lesson data — three lessons with full content

export interface FreeLessonSection {
  heading?: string;
  paragraphs: string[];
  dividerBefore?: boolean;
}

export interface FreeLessonBlock {
  type: 'situation' | 'prompt' | 'why' | 'anna' | 'try' | 'main' | 'nextstep' | 'tryitnow';
  content?: string;
  // For 'main' blocks
  sections?: FreeLessonSection[];
  // For 'nextstep' blocks
  nextLabel?: string;
  nextSubtext?: string;
  // For 'tryitnow' blocks
  promptContent?: string;
  tryIntro?: string[];
  tryOutro?: string[];
}

export interface FreeLesson {
  id: string;
  number: number;
  title: string;
  blocks: FreeLessonBlock[];
}

export const FREE_LESSONS: FreeLesson[] = [
  // ─────────────────────────────────────────────────────────────────────────
  // FREE LESSON 1 — YOU ARE NOT BEHIND
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: 'free-1',
    number: 1,
    title: 'You Are Not Behind',
    blocks: [
      {
        type: 'situation',
        content:
          'Six months ago you were in a meeting where everyone talked about AI like it was obvious.\n\nChatGPT this. Copilot that. You nodded along. That evening you googled it, found nothing that made sense, closed the tab.\n\nOr maybe it was a headline. Or a colleague who seemed to find all of this effortless.\n\nWhatever the version — you\'re here now.\n\nAnd the first thing I want to tell you is this:\n\nYou are not behind.',
      },
      {
        type: 'main',
        sections: [
          {
            heading: 'NOT BEHIND. UNDERSERVED.',
            paragraphs: [
              'Here is what actually happened.',
              'AI became genuinely useful for everyday professional tasks around 2023. Almost everything written about it since then has been aimed at two audiences: technical people who want to understand how it works, and younger generalists who seemed to find it intuitive already.',
              'If you had twenty or thirty years of professional experience and wanted a plain English explanation at your level — there wasn\'t much.',
              'That is not your fault. That is an explanation problem. The tools are not complicated. Nobody explained them properly.',
              'That\'s what ThatClicked is for.',
            ],
          },
          {
            dividerBefore: true,
            paragraphs: [
              'One more thing before we go further.',
              'Twenty or thirty years of professional experience gives you things AI cannot replicate — judgment, context, relationships, and the ability to know when something is wrong even if you can\'t immediately say why.',
              'AI handles the time-consuming, repetitive parts. Your experience directs it.',
              'The people who will use AI best are not the ones who understand it most deeply. They\'re the ones who know what they want to achieve.',
              'That\'s you. Once you know how to use the tool.',
            ],
          },
        ],
      },
      {
        type: 'anna',
        content:
          'I spent twenty-five years in corporate life before AI arrived and made me feel, briefly and quietly, like I\'d missed a memo.\n\nI hadn\'t. I just needed someone to explain it properly. ThatClicked is that explanation — built by someone who figured it out the hard way so you don\'t have to.\n\nI\'m not a tech person. I\'m a person who learned the tech. There\'s a difference, and I think it matters.',
      },
      {
        type: 'nextstep',
        content: 'The second free lesson answers the question most people are too embarrassed to ask out loud.\n\nWhat is AI, actually? Not the technical version. Not the breathless version. Just a plain answer to a reasonable question.',
        nextLabel: 'Next Free Lesson: What AI Actually Is →',
        nextSubtext: 'Free Lesson 2 of 3 — no account needed.',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────
  // FREE LESSON 2 — WHAT AI ACTUALLY IS
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: 'free-2',
    number: 2,
    title: 'What AI Actually Is',
    blocks: [
      {
        type: 'situation',
        content:
          'The AI headlines have not been subtle.\n\nIt will change everything. It will take your job. It\'s smarter than humans. It\'s overhyped. It\'s the most important invention since the internet. It\'s already obsolete.\n\nVery hard to know what to make of any of this when nobody has stopped to explain what AI actually is in ordinary language.\n\nHere is the explanation I wish I\'d had at the start. It takes about three minutes.',
      },
      {
        type: 'main',
        sections: [
          {
            heading: 'THE PLAIN ENGLISH VERSION',
            paragraphs: [
              'AI — the kind you\'ll use at work and in life — is a system trained on an enormous amount of text.',
              'Books, articles, websites, documents. Billions of them. From all of that, it has learned patterns. How language works. What a useful answer to a given question tends to look like.',
              'When you type something into an AI tool, it uses those patterns to generate a response that is likely to be helpful given what you asked.',
              'That\'s the mechanism. It is not thinking the way you think. It is not conscious. It does not have opinions or an agenda.',
              'It is an extraordinarily capable pattern-matching system that has read more than anyone and can produce fluent, useful responses very quickly.',
              'For most professional purposes, that is exactly what you need.',
            ],
          },
          {
            heading: 'WHY IT FEELS DIFFERENT FROM EVERYTHING BEFORE',
            dividerBefore: true,
            paragraphs: [
              'Every tool you\'ve used in your career has done exactly what you told it to do. No more.',
              'AI is different because you can talk to it in ordinary language and it understands what you mean — not just what you literally said.',
              '"Help me write a polite but firm email to a client who has missed three deadlines without damaging the relationship."',
              'It will write that email. Not a template. An actual draft you can read, adjust, and send.',
              'That shift — from tool that executes to assistant that understands — is why AI feels genuinely different.',
            ],
          },
          {
            heading: 'TWO THINGS WORTH KNOWING EARLY',
            dividerBefore: true,
            paragraphs: [
              'It gets things wrong. AI sounds confident even when incorrect. Check what it produces, especially anything involving facts or figures. You are the quality check.',
              'It doesn\'t know you. Each session starts fresh. The more context you give it, the better it responds. We cover this properly in the next lesson.',
              'These aren\'t reasons not to use it. They\'re reasons to use it intelligently. Which is what you\'re here to learn.',
            ],
          },
        ],
      },
      {
        type: 'anna',
        content:
          'The moment AI stopped feeling overwhelming was when I stopped trying to understand how it worked and started thinking about what I wanted it to do.\n\nI don\'t understand how my car engine works either. I understand that when I want to go somewhere, I drive. I know it has limits. I know not to drive it into a lake.\n\nThat\'s roughly the level of technical understanding you need for AI tools too.\n\nWhat they are. What they\'re good at. What to watch out for. How to direct them toward what you need.\n\nUsing the tools is what makes you better at using the tools. Not reading about them.',
      },
      {
        type: 'nextstep',
        content: 'The third free lesson is the most practical.\n\nYou\'ll learn the one thing most people skip — how to actually talk to AI in a way that gets useful results. And you\'ll try it yourself on something real before the lesson ends.',
        nextLabel: 'Next Free Lesson: Try It Yourself →',
        nextSubtext: 'Free Lesson 3 of 3 — no account needed.',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────
  // FREE LESSON 3 — TRY IT YOURSELF
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: 'free-3',
    number: 3,
    title: 'Try It Yourself',
    blocks: [
      {
        type: 'situation',
        content:
          'You have opened the AI tool. There is a text box. There is a cursor blinking in it.\n\nAnd you have no idea what to type.\n\nThis is where most people\'s first experience with AI goes wrong. They type something vague and get something vague back. Then they conclude AI isn\'t as useful as everyone says.\n\nIt isn\'t the tool. It\'s the question.\n\nAI is not a search engine. The difference between a mediocre response and a genuinely useful one is almost always in the quality of what you asked.\n\nThis lesson shows you how to ask better questions. By the end of it you\'ll have tried it yourself on something real.',
      },
      {
        type: 'main',
        sections: [
          {
            heading: 'THREE THINGS THAT CHANGE EVERYTHING',
            paragraphs: [],
          },
          {
            heading: 'BE SPECIFIC',
            paragraphs: [
              'Vague question. Vague answer. Every time.',
              'Not useful: "Help me with this email."',
              'Useful: "Help me write a polite but firm reply to a client who has missed two deadlines. I want to express concern without damaging the relationship. Under 150 words."',
              'The second version tells AI what you need, the tone, the constraint, and the context. It gets a far better result.',
            ],
          },
          {
            heading: 'GIVE CONTEXT',
            dividerBefore: true,
            paragraphs: [
              'AI knows nothing about you unless you tell it.',
              '"I\'m a project manager at a mid-size company. I need to send an update to my board..."',
              'That one sentence changes everything that follows. Ten seconds to add. Dramatically better output.',
            ],
          },
          {
            heading: 'ITERATE',
            dividerBefore: true,
            paragraphs: [
              'If what comes back isn\'t right — ask it to try again. Tell it what to change.',
              '"That\'s close but too formal — can you make it sound more like a normal email?"',
              'That is a perfectly good second prompt. Most people never send one.',
            ],
          },
        ],
      },
      {
        type: 'tryitnow',
        tryIntro: [
          'Open your AI tool — ChatGPT, Copilot, Claude, whichever you have. Then try this:',
        ],
        promptContent:
          'I need to write an email to [describe the person and your relationship]. The situation is: [describe what you need to say]. The tone should be [professional / warm / direct — choose what fits]. Keep it under 150 words.',
        tryOutro: [
          'Fill in the brackets with something real from your working life.',
          'Read what comes back. It won\'t be perfect. That\'s fine — ask it to adjust something. Try one follow-up: "Make it a bit warmer" or "Cut it by half" or "Make the first line stronger."',
          'That exchange — prompt, response, refinement — is what using AI well actually looks like.',
          'You\'ve just done it.',
        ],
      },
      {
        type: 'anna',
        content:
          'The first time I got a genuinely useful response, I laughed out loud.\n\nNot because it was clever. Because I realised that every disappointing attempt before it was down to how I\'d asked. I\'d been treating it like Google. Typing fragments. Getting fragments back.\n\nThe prompt that finally worked had context, a specific ask, and a tone requirement. It took forty seconds to write.\n\nWhat came back saved me twenty minutes of work.\n\nForty seconds in. Twenty minutes saved. That ratio holds up pretty well across most tasks.',
      },
    ],
  },
];

// Legacy lesson data kept for the optional preview link
export interface LessonBlock {
  type: 'situation' | 'prompt' | 'why' | 'anna' | 'try';
  content: string;
}

export interface Lesson {
  id: string;
  number: string;
  title: string;
  locked: boolean;
  blocks: LessonBlock[];
}

export interface Module {
  id: string;
  number: string;
  title: string;
  lessons: Lesson[];
}

export const lessonData: Module[] = [
  {
    id: 'module-1',
    number: '01',
    title: 'Copilot at Work',
    lessons: [
      {
        id: 'lesson-1-1',
        number: '1.1',
        title: 'What Copilot Actually Is',
        locked: false,
        blocks: [
          {
            type: 'situation',
            content:
              "Your manager mentioned Copilot in the last all-hands. A few colleagues are already using it. You've heard the word a lot but you're not entirely sure what it actually does — or whether it's worth your time to find out.",
          },
          {
            type: 'prompt',
            content:
              'Explain what Microsoft Copilot is to someone who works in an office but doesn\'t have a technical background. Keep it short and practical.',
          },
          {
            type: 'why',
            content:
              "Copilot works best when you treat it like a knowledgeable colleague who happens to be available immediately. The clearest prompts get the clearest answers. Asking it to explain things \"for someone without a technical background\" tells it the level to pitch at — and it listens.",
          },
          {
            type: 'anna',
            content:
              "I spent two weeks avoiding Copilot because I assumed I'd have to understand how it worked before I could use it. I didn't. You really don't either. Think of it like email: you don't need to know how the servers work to send a message.",
          },
          {
            type: 'try',
            content:
              'Copy that prompt above and paste it into Copilot right now. Read the answer. Notice how it talks to you. That\'s the baseline — everything from here gets more useful.',
          },
        ],
      },
    ],
  },
];